import { useEffect, useState } from "react";
import axios from "./api";

import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";

import CallCenterDialer from "./components/dialer";
import DialPad from "./components/dial_pad";

export default function DialerPage({ dialerRef, token, session }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [pageLoaded, setPageLoaded] = useState(true);
  const [refreshPage, setRefreshPage] = useState(false);

  const [callCenter, setCallCenter] = useState({});
  const [contact, setContact] = useState(null);
  const [callOutcomes, setCallOutcomes] = useState([]);

  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [openOutcomeDialog, setOpenOutcomeDialog] = useState(false);

  const [openDialPad, setOpenDialPad] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  async function getContact() {
    try {
      setShowLoading(true);
      const result = await axios.get(
        `agents/call_centers/widgets/${dialerRef}/contact.json`,
      );
      setShowLoading(false);
      if (result.data.status === "success") {
        setContact(result.data.data.contact);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data.message);
        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Unable to complete request.");
      setOpen(true);
    }
  }

  async function submitOutcome() {
    const contactRef = contact.reference ?? contact.phone_number;
    if (!contactRef) {
      setSeverity("error");
      setErrMsg("No contact found.");
      setOpen(true);

      return;
    }

    if (selectedOutcome === null) {
      setSeverity("error");
      setErrMsg("Call outcome is required.");
      setOpen(true);

      return;
    }

    try {
      setOpenOutcomeDialog(false);
      setShowLoading(true);
      const result = await axios.post(
        "agents/call_centers/widgets/call/outcome.json",
        {
          reference: dialerRef,
          contact: contactRef,
          outcome: selectedOutcome,
        },
      );
      setShowLoading(false);
      if (result.data.status === "success") {
        setSeverity("success");
        setErrMsg(result.data.data.message);
        setOpen(true);

        setContact(null);
        setRefreshPage(!refreshPage);
      } else {
        setSeverity("error");
        setErrMsg(result.data.data.message);

        setOpen(true);
      }
    } catch (err) {
      setShowLoading(false);
      setSeverity("error");
      setErrMsg("Unable to complete request.");
      setOpen(true);
    }
  }

  return (
    <Container>
      <Box sx={{margin: "35px auto", maxWidth: "480px"}}>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={showLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Snackbar
          open={open}
          autoHideDuration={4000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={handleClose}
            severity={severity}
            sx={{ width: "100%" }}
          >
            {errorMsg}
          </Alert>
        </Snackbar>

        <Dialog open={openOutcomeDialog} fullWidth maxWidth="xs">
          <DialogTitle>Log Call Outcome</DialogTitle>

          <DialogContent>
            <Box mt={2}>
              <FormControl fullWidth>
                <InputLabel>Call Outcome</InputLabel>

                <Select
                  label="Call Outcome"
                  value={selectedOutcome || ""}
                  onChange={(e) => setSelectedOutcome(e.target.value)}
                >
                  {session.call_outcomes.map((record) => (
                    <MenuItem key={record.reference} value={record.reference}>
                      {record.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenOutcomeDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={submitOutcome}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            // minHeight: "100vh",
            // background: "linear-gradient(180deg, #f4f7fb 0%, #eef3f9 100%)",
            p: 1.5,
          }}
        >
          {pageLoaded && (
            <Box p={2}>
              <Box mb={3}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src="/logo_2.png"
                    alt="Logo"
                    sx={{
                      width: "100%",
                      maxWidth: 180,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
                <Typography variant="h5" align="center">
                  Laddar Voice
                </Typography>
              </Box>
              <Card
                sx={{
                  mt: 1.5,
                  borderRadius: 3,
                  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
                }}
              >
                <Box p={0}>
                  <CallCenterDialer
                    agentRef={token}
                    contact={contact}
                    onIncomingCall={() => {}}
                    onCallCompleted={(completedContact) => {
                      if (
                        session.call_outcomes.length <= 0 ||
                        completedContact === null
                      ) {
                        return;
                      }

                      setOpenOutcomeDialog(true);
                    }}
                  />
                </Box>
              </Card>

              {session.can_call && (
                <Box mt={5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} md={6} lg={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Icon>contact_phone</Icon>}
                        onClick={() => {
                          setContact(null);
                          getContact();
                        }}
                        sx={{
                          height: 46,
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        Contact
                      </Button>
                    </Grid>

                    <Grid item xs={6} md={6} lg={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Icon>dialpad</Icon>}
                        onClick={() => setOpenDialPad(true)}
                        disabled={!session.can_dial}
                        sx={{
                          height: 46,
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        Dial Pad
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <DialPad
                open={openDialPad}
                onClose={() => setOpenDialPad(false)}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                onSubmit={(number) => {
                  setContact({
                    name: number,
                    phone_number: number,
                  });

                  setOpenDialPad(false);
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}
